import {
  CateringSummaryData,
  CateringSummaryEvent,
} from '../services/dashboard/data/catering-summary.data';
import {
  cateringApprovalStatusLabel,
  mergeCateringPreferenceSummary,
  preferenceComboSummaryLabel,
} from '../constants/catering-summary-labels';

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function preferenceLines(preferences: Record<string, number> | null | undefined): string {
  const merged = mergeCateringPreferenceSummary(preferences);
  if (!Object.keys(merged).length) return '';
  return Object.keys(merged)
    .filter((key) => (merged[key] ?? 0) > 0)
    .sort((a, b) =>
      preferenceComboSummaryLabel(a).localeCompare(
        preferenceComboSummaryLabel(b),
        'da'
      )
    )
    .map(
      (key) =>
        `<span class="chip">${merged[key]} × ${escapeHtml(preferenceComboSummaryLabel(key))}</span>`
    )
    .join('');
}

function extrasLines(
  extras: Array<{ label: string; quantity: number; comment?: string }> | null | undefined
): string {
  if (!extras?.length) return '';
  return extras
    .map((extra) => {
      const comment = extra.comment ? ` — ${escapeHtml(extra.comment)}` : '';
      return `<span class="chip extra">${extra.quantity} × ${escapeHtml(extra.label)}${comment}</span>`;
    })
    .join('');
}

function formatDayDate(date: string): string {
  if (!date) return '';
  const parsed = new Date(`${date}T12:00:00`);
  if (isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString('da-DK', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

function formatEventTime(event: CateringSummaryEvent): string {
  const start = event.start_time || '';
  const end = event.end_time || '';
  if (start && end && start !== end) return `${start} – ${end}`;
  return start || end || '—';
}

function buildEventBlock(event: CateringSummaryEvent): string {
  const prefs = preferenceLines(event.preferences);
  const extras = extrasLines(event.extras);
  const chips = [prefs, extras].filter(Boolean).join('');
  const comment = event.catering_order_comment
    ? `<p class="comment"><strong>Kommentar:</strong> ${escapeHtml(event.catering_order_comment)}</p>`
    : '';

  return `
    <article class="event">
      <div class="event-head">
        <h4>${escapeHtml(event.event_name || 'Event')}</h4>
        <span class="badge">${escapeHtml(cateringApprovalStatusLabel(event.approval_status))}</span>
      </div>
      <ul>
        ${event.location ? `<li><strong>Lokation:</strong> ${escapeHtml(event.location)}</li>` : ''}
        <li><strong>Lokale:</strong> ${escapeHtml(event.event_room || '—')}</li>
        <li><strong>Forplejning i:</strong> ${escapeHtml(event.catering_room_name || 'Standardlokale')}</li>
        <li><strong>Tid:</strong> ${escapeHtml(formatEventTime(event))}</li>
        <li><strong>Deltagere:</strong> ${event.participant_count ?? event.current_pending_user_count ?? 0}</li>
        ${event.customer_name ? `<li><strong>Kunde:</strong> ${escapeHtml(event.customer_name)}</li>` : ''}
      </ul>
      ${chips ? `<div class="chips">${chips}</div>` : ''}
      ${comment}
    </article>
  `;
}

export function buildCateringSummaryPdfHtml(
  data: CateringSummaryData,
  title = 'Forplejningsoversigt'
): string {
  const period = data.period;
  const periodLabel = period?.start && period?.end
    ? `${period.start} – ${period.end} (${period.days} ${period.days === 1 ? 'dag' : 'dage'})`
    : '';

  const totalPrefs = preferenceLines(data.totals?.preferences);
  const totalExtras = extrasLines(data.totals?.extras);
  const totalsBlock =
    totalPrefs || totalExtras
      ? `<section class="totals"><h3>Total bestilling</h3><div class="chips">${totalPrefs}${totalExtras}</div></section>`
      : '';

  const daysHtml = (data.days || [])
    .map((day) => {
      const dayPrefs = preferenceLines(day.totals?.preferences);
      const dayExtras = extrasLines(day.totals?.extras);
      const dayTotals =
        dayPrefs || dayExtras
          ? `<div class="chips day-totals">${dayPrefs}${dayExtras}</div>`
          : '';
      const eventsHtml = (day.events || []).map(buildEventBlock).join('');
      return `
        <section class="day">
          <h3>${escapeHtml(formatDayDate(day.date))}</h3>
          <p class="day-meta">${day.events?.length || 0} event(s)</p>
          ${dayTotals}
          ${eventsHtml || '<p class="empty">Ingen events</p>'}
        </section>
      `;
    })
    .join('');

  return `<!DOCTYPE html>
<html lang="da">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    body { font-family: Arial, sans-serif; color: #202124; margin: 24px; line-height: 1.45; }
    h1 { margin: 0 0 8px; font-size: 22px; }
    .meta { color: #5f6368; margin-bottom: 20px; font-size: 13px; }
    h3 { margin: 0 0 8px; font-size: 16px; }
    .totals, .day { margin-bottom: 24px; page-break-inside: avoid; }
    .day-meta { margin: 0 0 10px; color: #5f6368; font-size: 12px; }
    .event { border: 1px solid #dadce0; border-radius: 8px; padding: 12px; margin: 10px 0; }
    .event-head { display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; }
    .event h4 { margin: 0; font-size: 14px; }
    .badge { background: #f1f3f4; border-radius: 999px; padding: 2px 8px; font-size: 11px; white-space: nowrap; }
    ul { margin: 8px 0; padding-left: 18px; font-size: 12px; }
    .chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
    .chip { display: inline-block; background: #e8f0fe; color: #174ea6; border-radius: 999px; padding: 3px 10px; font-size: 11px; }
    .chip.extra { background: #fce8b2; color: #8a4b00; }
    .comment { margin: 8px 0 0; font-size: 12px; background: #f8f9fa; padding: 8px; border-radius: 6px; }
    .empty { color: #5f6368; font-size: 12px; }
    @media print { body { margin: 12mm; } }
  </style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  <div class="meta">${escapeHtml(periodLabel)} · Eksporteret ${new Date().toLocaleString('da-DK')}</div>
  ${totalsBlock}
  ${daysHtml || '<p class="empty">Ingen forplejningsevents i den valgte periode.</p>'}
</body>
</html>`;
}

export function downloadCateringSummaryPdf(
  data: CateringSummaryData | null | undefined,
  title = 'Forplejningsoversigt'
): void {
  if (!data) return;

  const html = buildCateringSummaryPdfHtml(data, title);
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
}
